import { Component, type ErrorInfo, type ReactNode } from 'react';
import ServiceNotice from './ServiceNotice';
import { isStaleBuildError, reloadIntoCurrentVersion } from '@/lib/staleBuild';

interface Props {
  children: ReactNode;
  /** Changing this value (e.g. the route path) clears a previous error. */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  /** The page is older than the last deploy: it is reloading into the current version. */
  reloading: boolean;
}

/**
 * Catches anything that breaks while rendering a page and shows the polite WhatsApp notice instead of a
 * blank screen. A page chunk that fails to download after a new deploy is not an error for the visitor:
 * the site reloads into the current version once (see lib/staleBuild). Header and footer stay usable
 * because the boundary only wraps the routes.
 */
export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reloading: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, reloading: isStaleBuildError(error) };
  }

  componentDidMount() {
    // Vite reports here the files of a lazy page that fail to preload (same cause, before the import throws).
    window.addEventListener('vite:preloadError', this.onPreloadError);
  }

  componentWillUnmount() {
    window.removeEventListener('vite:preloadError', this.onPreloadError);
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.state.reloading && reloadIntoCurrentVersion()) return;
    if (this.state.reloading) this.setState({ reloading: false });
    console.error('Page failed to render', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) this.setState({ hasError: false, reloading: false });
  }

  private onPreloadError = (event: Event) => {
    if (reloadIntoCurrentVersion()) event.preventDefault();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.reloading) {
      return (
        <div className="pt-header pb-16 px-4 min-h-[70vh] flex items-center justify-center" aria-busy="true">
          <i className="ri-loader-4-line animate-spin text-3xl text-reef" aria-hidden="true" />
        </div>
      );
    }
    return (
      <div className="pt-header pb-16 px-4 min-h-[70vh] flex items-center">
        <ServiceNotice subject="page" variant="page" onRetry={() => window.location.reload()} />
      </div>
    );
  }
}
